import { AddressInputType, PairEyewearValidationData, ShippingAddress, TypeSessionUser } from "@/common/lib/types";
import { create } from "zustand";

export type ShippingDetails = {
  delivery: string;
  id?: string;
} & AddressInputType;

export type AvailableSocialLogins = "facebook.com" | "google.com" | "apple.com";

type States = {
  wishlistedProducts: string[];
  isAuthenticated: boolean;
  user: TypeSessionUser | null;
  cartCount: number;
  cartVariantIds: string[];
  isLoadingCart: boolean;
  shippingDetails: null | ShippingDetails;
  toEditAddress: ShippingAddress | null;
  availableSocialLogins: AvailableSocialLogins[];
  hasPrescriptions?: boolean;
  isEditingShippingDetails: boolean;
  paymentMethod: string | null;
  cardDetails: {
    cardHolder: string;
    cardNumber: string;
    cvv: string;
    expiryDate: string;
    isShippingSameAsBilling: boolean;
    isSaveCard: boolean;
    type?: string;
  } | null;
  paymentByCardActiveTab: number;
  savedPaymentCardId: string | null;
  pairEyewearValidationData: PairEyewearValidationData | null;
  cartUpdatedAt: number;
};

type Actions = {
  removeProductFromWishlist: (id: string) => void;
  addProductToWishlist: (id: string) => void;
  clearWishlistedProducts: () => void;
  initWishlist: (list: string[]) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setUser: (user: TypeSessionUser | null) => void;
  updateCartCount: (count: number) => void;
  updateCartVariantIds: (ids: string[]) => void;
  addCartVariantId: (id: string) => void;
  setCartLoading: (loading: boolean) => void;
  setShippingDetails: (addr: ShippingDetails | null) => void;
  setToEditAddress: (address: ShippingAddress | null) => void;
  setAvailableSocialLogins: (available: AvailableSocialLogins[]) => void;
  setHasPrescription: (value: boolean) => void;
  setIsEditingShippingDetails: (value: boolean) => void;
  setPaymentMethod: (methos: string | null) => void;
  setCardDetails: (details: {
    cardHolder: string;
    cardNumber: string;
    cvv: string;
    expiryDate: string;
    isShippingSameAsBilling: boolean;
    isSaveCard: boolean;
    type?: string;
  }) => void;
  setPaymentByCardActiveTab: (tab: number) => void;
  setSavedPaymentCardId: (id: string | null) => void;
  setPairEyewearValidationData: (data: PairEyewearValidationData | null) => void;
  setCartUpdatedAt: (timestamp: number) => void;
  dismissPairEyewearFrame: (model: string, url: string) => void;
  dismissAllPairEyewearTops: () => void;
  dismissAllPairEyewearEntries: () => void;
};

export type Store = States & Actions;

export const useGlobalStore = create<Store>((set) => ({
  wishlistedProducts: [],
  isAuthenticated: false,
  user: null,
  shippingDetails: null,
  toEditAddress: null,
  availableSocialLogins: [],
  isEditingShippingDetails: false,
  paymentMethod: null,
  cardDetails: null,
  paymentByCardActiveTab: 0,
  savedPaymentCardId: null,
  pairEyewearValidationData: null,
  cartUpdatedAt: 0,

  setIsEditingShippingDetails: (value) =>
    set((state) => ({ ...state, isEditingShippingDetails: value })),
  setHasPrescription: (value) => set((state) => ({ ...state, hasPrescriptions: value })),
  setToEditAddress: (addr) => set((prevState) => ({ ...prevState, toEditAddress: addr })),
  setShippingDetails: (addr) => set((prevState) => ({ ...prevState, shippingDetails: addr })),
  setPaymentMethod: (method) => set((prevState) => ({ ...prevState, paymentMethod: method })),
  setCardDetails: (details) => set((prevState) => ({ ...prevState, cardDetails: details })),
  setPaymentByCardActiveTab: (tab) =>
    set((prevState) => ({ ...prevState, paymentByCardActiveTab: tab })),
  setSavedPaymentCardId: (id) => set((prevState) => ({ ...prevState, savedPaymentCardId: id })),

  setIsAuthenticated: (isAuthenticated) => set((prevState) => ({ ...prevState, isAuthenticated })),

  setUser: (user) => set((prevState) => ({ ...prevState, user })),
  cartCount: 0,
  cartVariantIds: [],
  isLoadingCart: false,

  // wish list actions
  initWishlist: (list) => set((prevState) => ({ ...prevState, wishlistedProducts: list })),

  addProductToWishlist: (id) =>
    set((prevState) => ({
      ...prevState,
      wishlistedProducts: [...prevState.wishlistedProducts!, id],
    })),

  clearWishlistedProducts: () => set((prevState) => ({ ...prevState, wishlistedProducts: [] })),

  removeProductFromWishlist: (id) =>
    set((prevState) => ({
      ...prevState,
      wishlistedProducts: prevState?.wishlistedProducts?.filter((prodId: string) => prodId !== id),
    })),

  // cart actions
  setCartLoading: (loading) => set((prevSatae) => ({ ...prevSatae, isLoadingCart: loading })),
  updateCartCount: (count) => set((prevState) => ({ ...prevState, cartCount: count })),
  updateCartVariantIds: (ids) => set((prevState) => ({ ...prevState, cartVariantIds: ids })),
  addCartVariantId: (id) =>
    set((prevState) => ({
      ...prevState,
      cartVariantIds: prevState.cartVariantIds.includes(id)
        ? prevState.cartVariantIds
        : [...prevState.cartVariantIds, id],
    })),
  setAvailableSocialLogins: (available: AvailableSocialLogins[] = []) => {
    set((prev) => ({
      ...prev,
      availableSocialLogins: available,
    }));
  },

  setPairEyewearValidationData: (data) =>
    set((prev) => ({ ...prev, pairEyewearValidationData: data })),

  setCartUpdatedAt: (timestamp) => set((prev) => ({ ...prev, cartUpdatedAt: timestamp })),

  dismissPairEyewearFrame: (model, url) =>
    set((prev) => {
      if (!prev.pairEyewearValidationData) return prev;
      return {
        ...prev,
        pairEyewearValidationData: {
          ...prev.pairEyewearValidationData,
          missingFrameModel: prev.pairEyewearValidationData.missingFrameModel.map((f) =>
            f.model.toLowerCase() === model.toLowerCase() && f.url === url
              ? { ...f, isDismissed: true }
              : f,
          ),
        },
      };
    }),

  dismissAllPairEyewearTops: () =>
    set((prev) => {
      if (!prev.pairEyewearValidationData) return prev;
      return {
        ...prev,
        pairEyewearValidationData: {
          ...prev.pairEyewearValidationData,
          missingTopModel: prev.pairEyewearValidationData.missingTopModel.map((t) => ({
            ...t,
            isDismissed: true,
          })),
        },
      };
    }),

  dismissAllPairEyewearEntries: () =>
    set((prev) => {
      if (!prev.pairEyewearValidationData) return prev;
      return {
        ...prev,
        pairEyewearValidationData: {
          ...prev.pairEyewearValidationData,
          missingTopModel: prev.pairEyewearValidationData.missingTopModel.map((t) => ({
            ...t,
            isDismissed: true,
          })),
          missingFrameModel: prev.pairEyewearValidationData.missingFrameModel.map((f) => ({
            ...f,
            isDismissed: true,
          })),
        },
      };
    }),
}));
